import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiFunnelFill } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';

import { cn } from '#/lib/utils';
import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#/modules/commonComponents/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '#/modules/commonComponents/shadcn/ui/command';
import {
  filterMainOptions,
  filterSubGroups,
  flattenSubOptions,
  fromTree,
} from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import { mtrStructTreeQueryKey } from '#/utils/queryKeys';
import {
  coerceFilterValues,
  resolveMultiFilterSummary,
  toggleMultiFilterValue,
} from '#/modules/commonComponents/table/columnFilterUtils';
import MultiSelectFilterList from '#/modules/commonComponents/table/MultiSelectFilterList';

const EMPTY_META = {};
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_TREE_LOADER = () => fetchMtrStructTree({ status: 'ACTIVE' });

function resolveOptionLabel(options, value) {
  const match = options.find((option) => option.value === value);
  return match?.label ?? value;
}

function resolveTriggerLabel(value, options) {
  if (value === undefined || value === null || value === '') {
    return '全部';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '全部';
    }

    if (value.length === 1) {
      const label = resolveOptionLabel(options, value[0]);
      return label === value[0] ? '已選 1 項' : label;
    }

    return `已選 ${value.length} 項`;
  }

  const label = resolveOptionLabel(options, value);
  return label === value ? String(value) : label;
}

function resolveOptions(column, meta) {
  if (meta.facet === 'fixed') {
    return meta.options || [];
  }

  const facetedValues = column.getFacetedUniqueValues?.();
  if (!facetedValues) {
    return [];
  }

  return Array.from(facetedValues.keys())
    .filter((item) => item !== undefined && item !== null && item !== '')
    .map((item) => ({
      value: String(item),
      label: String(item),
    }));
}

function isRequestAborted(error, signal) {
  return signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';
}

function ColumnFilterPopover({ column }) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const meta = column.columnDef.meta ?? EMPTY_META;
  const filterVariant = meta.filterVariant;
  const isServerMode = meta.filterMode === 'server';
  const isAsyncFacet = meta.facet === 'async';
  const isTreeFacet = meta.facet === 'tree';
  const treeRole = meta.treeRole;
  const disabled = Boolean(meta.filterDisabled);
  const searchPlaceholder = meta.searchPlaceholder || '搜尋代號或名稱';
  const searchDebounceMs = meta.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS;
  const treeLoader = meta.treeLoader ?? DEFAULT_TREE_LOADER;
  const treeQueryKey = meta.treeQueryKey ?? mtrStructTreeQueryKey();

  const filterValue = isServerMode ? meta.filterValue : column.getFilterValue();
  const isMultiSelect = filterVariant === 'selectMulti';
  const selectedValues = useMemo(
    () => (isMultiSelect ? coerceFilterValues(filterValue) : []),
    [isMultiSelect, filterValue],
  );
  const hasActiveFilter = isMultiSelect
    ? selectedValues.length > 0
    : (Array.isArray(filterValue) ? filterValue.length > 0 : Boolean(filterValue));

  const treeQuery = useQuery({
    queryKey: treeQueryKey,
    queryFn: treeLoader,
    enabled: isTreeFacet && (open || hasActiveFilter),
  });

  const treeRows = useMemo(
    () => treeQuery.data?.data ?? [],
    [treeQuery.data?.data],
  );

  const structFilterModel = useMemo(
    () => fromTree(treeRows),
    [treeRows],
  );

  const treeMainOptions = useMemo(() => {
    if (!isTreeFacet || treeRole !== 'main') {
      return [];
    }

    return filterMainOptions(structFilterModel, inputValue);
  }, [isTreeFacet, treeRole, structFilterModel, inputValue]);

  const treeSubGroups = useMemo(() => {
    if (!isTreeFacet || treeRole !== 'sub') {
      return [];
    }

    return filterSubGroups(structFilterModel, inputValue);
  }, [isTreeFacet, treeRole, structFilterModel, inputValue]);

  const treeLabelOptions = useMemo(() => {
    if (!isTreeFacet) {
      return [];
    }

    if (treeRole === 'main') {
      return structFilterModel.mains;
    }

    return flattenSubOptions(structFilterModel);
  }, [isTreeFacet, treeRole, structFilterModel]);

  const fixedOptions = resolveOptions(column, meta);
  const treeDisplayOptions = treeRole === 'main'
    ? treeMainOptions
    : treeSubGroups.flatMap((group) => group.subs);
  const options = isAsyncFacet
    ? asyncOptions
    : isTreeFacet
      ? treeDisplayOptions
      : fixedOptions;
  const triggerLabelOptions = isTreeFacet ? treeLabelOptions : options;
  const triggerLabel = isMultiSelect
    ? resolveMultiFilterSummary(filterValue, triggerLabelOptions)
    : resolveTriggerLabel(filterValue, triggerLabelOptions);

  const searchOptions = meta.searchOptions;
  const dependsOnFilters = meta.dependsOnFilters;

  const runSearch = useCallback(async (keyword) => {
    if (!searchOptions) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsSearching(true);

    try {
      const results = await searchOptions({
        keyword,
        parentFilters: dependsOnFilters,
        signal: controller.signal,
      });

      if (!controller.signal.aborted) {
        setAsyncOptions(results ?? []);
      }
    } catch (error) {
      if (!isRequestAborted(error, controller.signal)) {
        setAsyncOptions([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsSearching(false);
      }
    }
  }, [searchOptions, dependsOnFilters]);

  const handleOpenChange = useCallback((nextOpen) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      abortRef.current?.abort();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      return;
    }

    if (isAsyncFacet || isTreeFacet) {
      setInputValue('');
    }

    if (isAsyncFacet) {
      runSearch('');
    }
  }, [isAsyncFacet, isTreeFacet, runSearch]);

  useEffect(() => {
    if (!open || !isAsyncFacet) {
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      runSearch(inputValue);
    }, searchDebounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, open, isAsyncFacet, runSearch, searchDebounceMs]);

  function commitFilter(nextValue) {
    if (isServerMode) {
      if (isAsyncFacet && meta.rememberSelectedOptions) {
        meta.rememberSelectedOptions(
          asyncOptions,
          coerceFilterValues(nextValue),
        );
      }
      meta.onServerFilterChange?.(nextValue);
      return;
    }

    column.setFilterValue(nextValue);
  }

  function handleSingleSelect(optionValue) {
    if (filterValue === optionValue) {
      commitFilter(undefined);
      return;
    }

    commitFilter(optionValue);
    setOpen(false);
  }

  function handleMultiToggle(optionValue) {
    commitFilter(toggleMultiFilterValue(filterValue, optionValue));
  }

  function handleClearMulti() {
    commitFilter(undefined);
  }

  const showFixedSearch = !isAsyncFacet && !isTreeFacet
    && (filterVariant === 'selectMulti' || options.length > 8);

  function renderMultiOption(option, itemClassName = '') {
    const checked = selectedValues.includes(option.value);

    return (
      <CommandItem
        key={option.value != null && option.value !== ''
          ? `${String(option.value)}-${option.label}`
          : `opt-${option.label}`}
        value={option.label}
        className={cn('[&>svg:last-child]:hidden', itemClassName)}
        onSelect={() => handleMultiToggle(option.value)}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => {}}
          className="pointer-events-none"
          tabIndex={-1}
        />
        <span>{option.label}</span>
      </CommandItem>
    );
  }

  function renderMultiOptions() {
    return options.map((option) => renderMultiOption(option));
  }

  function renderTreeSubGroups() {
    return treeSubGroups.map((group, index) => (
      <div
        key={group.mainId}
        className={cn(
          'mx-1.5 mb-2 overflow-hidden rounded-md border border-gray-200 bg-slate-50',
          index > 0 && 'mt-1',
          index === treeSubGroups.length - 1 && 'mb-0',
        )}
      >
        <CommandGroup
          heading={group.mainLabel}
          className={cn(
            '[&_[cmdk-group-heading]]:border-b [&_[cmdk-group-heading]]:border-gray-200',
            '[&_[cmdk-group-heading]]:bg-slate-100 [&_[cmdk-group-heading]]:px-2.5',
            '[&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs',
            '[&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-gray-600',
          )}
        >
          {group.subs.map((option) => renderMultiOption(option, 'pl-2'))}
        </CommandGroup>
      </div>
    ));
  }

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn(
              'h-7 max-w-full gap-1 px-2 text-xs font-normal',
              hasActiveFilter && 'border-slate-400 bg-slate-100 text-slate-700',
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <PiFunnelFill className="size-3 shrink-0" />
            <span className="truncate">{triggerLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn(
            'z-[60] p-0',
            isTreeFacet && treeRole === 'sub' ? 'w-72' : 'w-56',
          )}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          {filterVariant === 'selectSingle' && !isAsyncFacet && !isTreeFacet ? (
            <Command>
              {showFixedSearch ? <CommandInput placeholder="搜尋..." /> : null}
              <CommandList
                className="max-h-60 min-h-0"
                onWheel={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
              >
                <CommandEmpty>無符合項目</CommandEmpty>
                <CommandGroup>
                  {options.map((option, index) => (
                    <CommandItem
                      key={option.value != null && option.value !== ''
                        ? `${String(option.value)}-${index}`
                        : `single-${index}`}
                      value={option.label}
                      onSelect={() => handleSingleSelect(option.value)}
                    >
                      <span className={cn(filterValue === option.value && 'font-medium text-primary-600')}>
                        {option.label}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : null}

          {filterVariant === 'selectSingle' && isAsyncFacet ? (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={searchPlaceholder}
                value={inputValue}
                onValueChange={setInputValue}
              />
              <CommandList
                className="max-h-60 min-h-0"
                onWheel={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
              >
                {isSearching ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">搜尋中...</div>
                ) : null}
                <CommandEmpty>無符合項目</CommandEmpty>
                <CommandGroup>
                  {asyncOptions.map((option, index) => (
                    <CommandItem
                      key={option.value != null && option.value !== ''
                        ? `${String(option.value)}-${index}`
                        : `async-${index}`}
                      value={option.label}
                      onSelect={() => handleSingleSelect(option.value)}
                    >
                      <span
                        className={cn(
                          filterValue === option.value && 'font-medium text-primary-600',
                        )}
                      >
                        {option.label}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : null}

          {filterVariant === 'selectMulti' && !isAsyncFacet && !isTreeFacet ? (
            <MultiSelectFilterList
              showCommandInput={showFixedSearch}
              onClear={handleClearMulti}
            >
              <CommandGroup>
                {renderMultiOptions()}
              </CommandGroup>
            </MultiSelectFilterList>
          ) : null}

          {filterVariant === 'selectMulti' && isAsyncFacet ? (
            <MultiSelectFilterList
              shouldFilter={false}
              showCommandInput
              commandInputPlaceholder={searchPlaceholder}
              commandInputValue={inputValue}
              onCommandInputValueChange={setInputValue}
              isLoading={isSearching}
              loadingText="搜尋中..."
              onClear={handleClearMulti}
            >
              <CommandGroup>
                {renderMultiOptions()}
              </CommandGroup>
            </MultiSelectFilterList>
          ) : null}

          {filterVariant === 'selectMulti' && isTreeFacet ? (
            <MultiSelectFilterList
              shouldFilter={false}
              showCommandInput
              commandInputPlaceholder={searchPlaceholder}
              commandInputValue={inputValue}
              onCommandInputValueChange={setInputValue}
              isLoading={treeQuery.isLoading}
              onClear={handleClearMulti}
            >
              {treeRole === 'main' ? (
                <CommandGroup>
                  {treeMainOptions.map((option) => renderMultiOption(option))}
                </CommandGroup>
              ) : (
                renderTreeSubGroups()
              )}
            </MultiSelectFilterList>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

ColumnFilterPopover.propTypes = {
  column: PropTypes.shape({
    columnDef: PropTypes.shape({
      meta: PropTypes.object,
    }),
    getFilterValue: PropTypes.func,
    setFilterValue: PropTypes.func,
    getFacetedUniqueValues: PropTypes.func,
  }).isRequired,
};

export default ColumnFilterPopover;
